/**
 * shared/space_intel.ts — CLIENT MIRROR of server/src/lib/space_intel.ts
 * (the server copy is authoritative; keep them byte-identical below the
 * headers). Space Wave 5 — observation-driven information: the intel
 * payload, freshness, the chart's newest-per-system view, the scout pay
 * rule, the bookmark limits. Pure.
 */
// ── THE INTEL LAW ────────────────────────────────────────────────────────────
export const INTEL_FRESH_S = 7_200;
export const INTEL_STALE_S = 86_400;
export const INTEL_REAP_S = 7 * 86_400;
export const INTEL_SENSORS = ['presence', 'scan', 'dock', 'report'] as const;
export type IntelSensor = (typeof INTEL_SENSORS)[number];
export type Freshness = 'fresh' | 'stale' | 'gone';

/** What the server wrote from the truth at observation time. */
export interface IntelPayload {
  control: 'aurelian' | 'borean' | 'commons';
  score: number;
  incursionGrip: number | null;
  belts: Array<{ key: string; remaining: number; capacity: number }>;
  campaign: { state: string; fortKey: string; aCaps: number; bCaps: number } | null;
  wormholes: string[];
  priceMods: Array<{ stationKey: string; commodity: string; mult: number }>;
}
export function emptyIntel(): IntelPayload {
  return { control: 'commons', score: 0, incursionGrip: null, belts: [], campaign: null, wormholes: [], priceMods: [] };
}
/** Never throws: garbage reads as an empty payload. */
export function parseIntel(json: string): IntelPayload {
  try {
    const o = JSON.parse(json) as Partial<IntelPayload> | null;
    if (!o || typeof o !== 'object') return emptyIntel();
    return {
      control: o.control === 'aurelian' || o.control === 'borean' ? o.control : 'commons',
      score: typeof o.score === 'number' ? o.score : 0,
      incursionGrip: typeof o.incursionGrip === 'number' ? o.incursionGrip : null,
      belts: Array.isArray(o.belts) ? o.belts.filter((b) => b && typeof b.key === 'string') : [],
      campaign: o.campaign && typeof o.campaign === 'object' ? o.campaign : null,
      wormholes: Array.isArray(o.wormholes) ? o.wormholes.filter((w) => typeof w === 'string') : [],
      priceMods: Array.isArray(o.priceMods) ? o.priceMods.filter((m) => m && typeof m.stationKey === 'string') : [],
    };
  } catch { return emptyIntel(); }
}
export function intelFreshness(observedMicros: bigint, nowMicros: bigint): Freshness {
  const age = Number(nowMicros - observedMicros) / 1_000_000;
  return age < INTEL_FRESH_S ? 'fresh' : age < INTEL_STALE_S ? 'stale' : 'gone';
}
export function ageLabel(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86_400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86_400)} d ago`;
}
export interface IntelRowLike { systemId: string; sensor: string; observedAt: { microsSinceUnixEpoch: bigint }; payload: string; civ: string }
/** The chart's view: the newest observation per system (any sensor). */
export function newestPerSystem<T extends IntelRowLike>(rows: Iterable<T>): Map<string, T> {
  const out = new Map<string, T>();
  for (const r of rows) {
    const cur = out.get(r.systemId);
    if (!cur || r.observedAt.microsSinceUnixEpoch > cur.observedAt.microsSinceUnixEpoch) out.set(r.systemId, r);
  }
  return out;
}

// ── THE SCOUT LAW ────────────────────────────────────────────────────────────
export const SCOUT_PAY_SOV = 40n;
export const SCOUT_LOYALTY = 15n;
/** A report pays when the civ holds no `report` on the system fresher than INTEL_FRESH_S (the Commons never pays). */
export function scoutPays(civRows: Iterable<IntelRowLike>, systemId: string, nowMicros: bigint): boolean {
  if (systemId === 'sys0') return false;
  for (const r of civRows) {
    if (r.systemId !== systemId || r.sensor !== 'report') continue;
    if (Number(nowMicros - r.observedAt.microsSinceUnixEpoch) / 1_000_000 < INTEL_FRESH_S) return false;
  }
  return true;
}

// ── THE BOOKMARK LAW ─────────────────────────────────────────────────────────
export const BOOKMARKS_MAX = 40;
export const BOOKMARK_KINDS = ['spot', 'throat', 'wreck', 'belt', 'station'] as const;
export type BookmarkKind = (typeof BOOKMARK_KINDS)[number];
export const BOOKMARK_LABEL_MAX = 40;
